import React from "react";
import { motion } from "framer-motion";
import { Clock } from "lucide-react";

interface BillableHoursProgressProps {
  today: { minutes: number; targetMinutes: number };
  week: { minutes: number; targetMinutes: number };
  month: { minutes: number; targetMinutes: number };
}

interface ProgressRowProps {
  label: string;
  minutes: number;
  targetMinutes: number;
  delay: number;
}

const getBarColor = (pct: number): string => {
  if (pct > 100) return "bg-red-500";
  if (pct >= 90) return "bg-amber-500";
  return "bg-emerald-500";
};

const ProgressRow: React.FC<ProgressRowProps> = ({
  label,
  minutes,
  targetMinutes,
  delay,
}) => {
  const hours = (minutes / 60).toFixed(1);
  const targetHours = (targetMinutes / 60).toFixed(1);
  const pct = targetMinutes > 0 ? (minutes / targetMinutes) * 100 : 0;
  const clampedPct = Math.min(pct, 100);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-slate-700">{label}</span>
        <span className="text-slate-500">
          <span className="font-semibold text-slate-800">{hours}h</span>{" "}
          / {targetHours}h
        </span>
      </div>
      <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden">
        <motion.div
          className={`h-full rounded-full ${getBarColor(pct)}`}
          initial={{ width: 0 }}
          animate={{ width: `${clampedPct}%` }}
          transition={{ duration: 0.8, delay, ease: "easeOut" }}
        />
      </div>
    </div>
  );
};

const BillableHoursProgress: React.FC<BillableHoursProgressProps> = ({
  today,
  week,
  month,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-5">
        <div className="p-2 rounded-lg" style={{ backgroundColor: "#0F51321A" }}>
          <Clock className="h-4 w-4" style={{ color: "#0F5132" }} />
        </div>
        <h3 className="text-sm font-semibold text-slate-700 m-0 leading-none">Billable Hours</h3>
      </div>

      <div className="space-y-4">
        <ProgressRow
          label="Today"
          minutes={today.minutes}
          targetMinutes={today.targetMinutes}
          delay={0}
        />
        <ProgressRow
          label="This Week"
          minutes={week.minutes}
          targetMinutes={week.targetMinutes}
          delay={0.1}
        />
        <ProgressRow
          label="This Month"
          minutes={month.minutes}
          targetMinutes={month.targetMinutes}
          delay={0.2}
        />
      </div>
    </motion.div>
  );
};

export default BillableHoursProgress;
